import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {SchedulingLinkModel} from '../../../../models/api/schedulingLinkModel';
import {SchedulingLinksAccessPointsEnum} from '../../../../services/tracking';
import {CalendarsPanelId, ComposerPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {SchedulingLinksListItemContainer} from '../../../calendars/scheduling/schedulingLinksListItemContainer';
import {DialogButtonGroup} from '../../../common/dialogs/dialogButtonGroup';
import {MeetingFormattingSettings} from './meetingsettings/meetingFormattingSettings';
import {MeetingTimesData} from './meetingsettings/meetingSettingsPanel';

/*
 * Props.
 */

interface SchedulingLinkInsertSlotsDropdownProps extends DropdownProps {
  panelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId;
  meetingTimesData: MeetingTimesData;
  schedulingLink: SchedulingLinkModel;
  onMeetingTimesDataChange: HandlerOf<MeetingTimesData | undefined>;
  onInsertMeetingTimes: HandlerOf<MeetingTimesData>;
  onBackButtonClick: Handler;
}

/*
 * Styles.
 */

const StyledSchedulingLinkListItemWrapperDiv = styled.div`
  padding: 4px;
`;

const StyledFormattingWrapperDiv = styled.div`
  padding: 5px 20px 20px 20px;
`;

/*
 * Constants.
 */

const dropdownWidth = 320;

/*
 * Component.
 */

export const SchedulingLinkInsertSlotsDropdown: FC<SchedulingLinkInsertSlotsDropdownProps> = (props) => (
  <Dropdown {...props} width={dropdownWidth}>
    <DropdownHeader
      title={<Trans id="schedulinglinks-dropdown-header-insertmeetingslots">Insert meeting slots</Trans>}
      leftIcon={
        <IconButton
          iconName="chevronLeftSmall"
          label={<Trans id="scheduling-link-insert-slots-back">Back</Trans>}
          onClick={props.onBackButtonClick}
        />
      }
    />
    <StyledSchedulingLinkListItemWrapperDiv>
      <SchedulingLinksListItemContainer
        panelId={props.panelId}
        schedulingLink={props.schedulingLink}
        isNewlyCreated={false}
        accessPoint={SchedulingLinksAccessPointsEnum.COMPOSER}
        shouldHideMoreMenu
      />
    </StyledSchedulingLinkListItemWrapperDiv>
    <StyledFormattingWrapperDiv>
      <MeetingFormattingSettings
        timezone={props.meetingTimesData.timezone}
        timeFormat={props.meetingTimesData.timeFormat}
        onTimezoneSelect={(newTimezone) => {
          props.onMeetingTimesDataChange({
            ...props.meetingTimesData,
            timezone: newTimezone,
          });
        }}
        onTimeFormatSelect={(newTimeFormat) => {
          props.onMeetingTimesDataChange({
            ...props.meetingTimesData,
            timeFormat: newTimeFormat,
          });
        }}
      />
    </StyledFormattingWrapperDiv>
    <DialogButtonGroup>
      <ButtonGroupSpacer />
      <Button
        variant={ButtonVariantsEnum.SECONDARY}
        size={VisualSizesEnum.MEDIUM}
        onClick={() => props.onMeetingTimesDataChange(undefined)}
      >
        <Trans id="dropdown-cancel">Cancel</Trans>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        size={VisualSizesEnum.MEDIUM}
        onClick={() => props.onInsertMeetingTimes(props.meetingTimesData)}
      >
        <Trans id="dropdown-insertmeetingslots">Insert meeting slots</Trans>
      </Button>
    </DialogButtonGroup>
  </Dropdown>
);
